export enum HubSpotOperator {
  IS_NOT_EMPTY = 'is_not_empty', // Meaning that property property is simply populated with any value.
  IS_EMPTY = 'is_empty', // Meaning that the field is simply not populated at all.
  EQ = 'eq', // Meaning that the property is equal to the value specified (as seen in the JSON sample provided).
  NEQ = 'neq', // Meaning that the property is not equal to the value specified (as seen in the JSON sample provided).
  CONTAINS = 'contains', // (For string properties only) Meaning that the property contains the value that you specify.
  LT = 'lt', // (For number properties only) Meaning that the property contains a value less than the one you specify.
  LTE = 'lte', // (For number properties only) Meaning that the property contains a value less than or equal to the one you specify.
  GT = 'gt', // (For number properties only) Meaning that the property contains a value greater than the one you specify.
  GTE = 'gte', // (For number properties only) Meaning that the property contains a value greater than or equal to the one you specify.
  SET_ANY = 'set_any', // (For enumerated properties only) Meaning that the property contains any of a semi-colon separated list of values.
  SET_NOT_ANY = 'set_not_any', // (For enumerated properties only) Meaning that the property doesn't contain any of a semi-colon separated list of values.
  SET_EQ = 'set_eq', // (For enumerated properties only) Meaning that the property has exactly the values in a semi-colon separated list.
  SET_NEQ = 'set_neq', // (For enumerated properties only) Meaning that the property doesn't have exactly the values in a semi-colon separated list.
  SET_ALL = 'set_all', // (For enumerated properties only) Meaning that the property has all of the values in a semi-colon separated list.
}

/**
 * We are not quite sure how HubSpot handles the values, so we do this to be safe.
 */
function getValue(value: string | string[]): string {
  return typeof value === 'object' ? value[0] : value;
}
function getValues(value: string | string[]): string[] {
  return typeof value === 'object' ? value : [value];
}

export const HubSpotOperatorToValidationFnMap: {
  [key in HubSpotOperator]: (parentValue: string | string[], targetValue: string | string[]) => boolean;
} = {
  [HubSpotOperator.IS_NOT_EMPTY]: parentValue => !!parentValue && parentValue !== '',
  [HubSpotOperator.IS_EMPTY]: parentValue => !parentValue || parentValue === '',
  [HubSpotOperator.EQ]: (parentValue, targetValue) => parentValue === targetValue,
  [HubSpotOperator.NEQ]: (parentValue, targetValue) => parentValue !== targetValue,
  [HubSpotOperator.CONTAINS]: (parentValue, targetValue) => parentValue.includes(getValue(targetValue)),
  [HubSpotOperator.LT]: (parentValue, targetValue) =>
    parseFloat(getValue(parentValue)) < parseFloat(getValue(targetValue)),
  [HubSpotOperator.LTE]: (parentValue, targetValue) =>
    parseFloat(getValue(parentValue)) <= parseFloat(getValue(targetValue)),
  [HubSpotOperator.GT]: (parentValue, targetValue) =>
    parseFloat(getValue(parentValue)) > parseFloat(getValue(targetValue)),
  [HubSpotOperator.GTE]: (parentValue, targetValue) =>
    parseFloat(getValue(parentValue)) >= parseFloat(getValue(targetValue)),
  [HubSpotOperator.SET_ANY]: (parentValue, targetValue) =>
    !!getValues(parentValue).find(value => getValues(targetValue).includes(value)),
  [HubSpotOperator.SET_NOT_ANY]: (parentValue, targetValue) =>
    !getValues(parentValue).find(value => getValues(targetValue).includes(value)),
  [HubSpotOperator.SET_EQ]: (parentValue, targetValue) =>
    getValues(parentValue).sort().join(';') === getValues(targetValue).sort().join(';'),
  [HubSpotOperator.SET_NEQ]: (parentValue, targetValue) =>
    getValues(parentValue).sort().join(';') !== getValues(targetValue).sort().join(';'),
  [HubSpotOperator.SET_ALL]: (parentValue, targetValue) =>
    getValues(targetValue).filter(values => !getValues(parentValue).includes(values)).length === 0,
};
